import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import Header from './Header'
import './NeuralGlassHomepage.css';
import { useUser } from '../contexts/UserContext';
import AIFeaturesSection from './AIFeaturesSection';
import Testimonials from './Testimonials';
import FAQ from './FAQ';
import Footer from './Footer';
import ProfileSetupModal from './ProfileSetupModal';
import ProductSidebar from './ProductSidebar';

const NeuralGlassHomepage = () => {
  const { user, login, logout, loading } = useUser();
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || window.location.origin;
  
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactForm, setContactForm] = useState({
  name: '',
  email: '',
  subject: '',
  message: ''
});
const [contactSubmitting, setContactSubmitting] = useState(false);
const [contactMessage, setContactMessage] = useState('');
const [showProfileModal, setShowProfileModal] = useState(false);
const [audioModal, setAudioModal] = useState(null); // 'mac' | 'windows' | null

  // Refs for DOM elements
  const headerRef = useRef(null);
  const mobileMenuToggleRef = useRef(null);
  const mobileNavRef = useRef(null);
  const submitBtnRef = useRef(null);

  // Define handleScroll with useCallback to maintain reference
  const handleScroll = useCallback(() => {
    const header = headerRef.current;
    if (!header) return;
    
    const scrolled = window.pageYOffset;
    
    if (scrolled > 50) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }
  }, []);

  // Define updateActiveMenuItem with useCallback
  const updateActiveMenuItem = useCallback(() => {
    const sections = document.querySelectorAll('section[id]');
    const navLinks = document.querySelectorAll('.nav-links a, .mobile-nav a');
    
    let currentSection = '';
    const scrollPos = window.pageYOffset + 100;
    
    sections.forEach(section => {
      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;
      
      if (scrollPos >= sectionTop && scrollPos < sectionTop + sectionHeight) {
        currentSection = section.getAttribute('id');
      }
    });
    
    navLinks.forEach(link => {
      link.classList.remove('active');
      if (link.getAttribute('href') === `#${currentSection}`) {
        link.classList.add('active');
      }
    });
  }, []);

  useEffect(() => {
    // Initialize animations after component mounts
    const initAnimations = () => {
      // Mobile menu functionality
      const mobileMenuToggle = mobileMenuToggleRef.current;
      const mobileNav = mobileNavRef.current;

      const scrollTarget = sessionStorage.getItem('scrollTarget');
      if (scrollTarget) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          const target = document.querySelector(scrollTarget);
          if (target) {
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
          sessionStorage.removeItem('scrollTarget');
        }, 300);
      }

      const toggleMenu = () => {
        mobileMenuToggle?.classList.toggle('active');
        mobileNav?.classList.toggle('active');
      };

      if (mobileMenuToggle) {
        mobileMenuToggle.addEventListener('click', toggleMenu);
      }

      // Close mobile menu when clicking on links
      const mobileNavLinks = document.querySelectorAll('.mobile-nav a');
      mobileNavLinks.forEach(link => {
        link.addEventListener('click', () => {
          if (mobileMenuToggle) mobileMenuToggle.classList.remove('active');
          if (mobileNav) mobileNav.classList.remove('active');
        });
      });

      // Close mobile menu when clicking outside
      const handleClickOutside = (e) => {
        if (mobileMenuToggle && !mobileMenuToggle.contains(e.target) && mobileNav && !mobileNav.contains(e.target)) {
          mobileMenuToggle.classList.remove('active');
          mobileNav.classList.remove('active');
        }
      };

      document.addEventListener('click', handleClickOutside);

      // Smooth scrolling for anchor links
      const handleAnchorClick = function (e) {
        e.preventDefault();
        const targetId = this.getAttribute('href');
        
        if (targetId === '#') return;
        
        const target = document.querySelector(targetId);
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      };

      const anchorLinks = document.querySelectorAll('a[href^="#"]');
      anchorLinks.forEach(anchor => {
        anchor.addEventListener('click', handleAnchorClick);
      });

      // Header scroll effect
      window.addEventListener('scroll', handleScroll);

      // Active menu item highlighting
      window.addEventListener('scroll', updateActiveMenuItem);
      window.addEventListener('load', updateActiveMenuItem);

      // Form submission effect
      const submitBtn = submitBtnRef.current;
      if (submitBtn) {
        const handleSubmitClick = function(e) {
          e.preventDefault();
          this.innerHTML = 'TRANSMITTING...';
          this.style.background = 'linear-gradient(45deg, #8000ff, #00ffff)';
          
          setTimeout(() => {
            this.innerHTML = 'TRANSMISSION COMPLETE';
            this.style.background = 'linear-gradient(45deg, #00ff00, #00ffff)';
            
            setTimeout(() => {
              this.innerHTML = 'TRANSMIT TO MATRIX';
              this.style.background = 'linear-gradient(45deg, #00ffff, #ff0080)';
            }, 2000);
          }, 1500);
        };

        submitBtn.addEventListener('click', handleSubmitClick);
      }

      // Return cleanup function
      return () => {
        if (mobileMenuToggle) {
          mobileMenuToggle.removeEventListener('click', toggleMenu);
        }
        document.removeEventListener('click', handleClickOutside);
        anchorLinks.forEach(anchor => {
          anchor.removeEventListener('click', handleAnchorClick);
        });
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('scroll', updateActiveMenuItem);
        window.removeEventListener('load', updateActiveMenuItem);
      };
    };

    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAnimations);
      return () => {
        document.removeEventListener('DOMContentLoaded', initAnimations);
      };
    } else {
      const cleanup = initAnimations();
      return cleanup;
    }
  }, [handleScroll, updateActiveMenuItem]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    console.log('🔍 Profile check fired, token:', token ? 'EXISTS' : 'NULL', 'user:', user?.email);
    if (!token) return;

    const checkProfile = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/profile/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        console.log('📋 Profile status data:', data);
        if (!data.profileComplete) {
          setShowProfileModal(true);
        }
      } catch (err) {
        console.error('Profile check failed:', err);
      }
    };

    checkProfile();
  }, [user]);

  const generateVerificationCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // FIXED: Handle logout properly
  const handleLogout = async () => {
    try {
      // Call the logout API endpoint
      const response = await fetch(`${API_BASE_URL}/api/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ email: user?.email })
      });

      const data = await response.json();
      
      if (response.ok) {
        console.log('Logout successful:', data);
      } else {
        console.log('Logout API error, but proceeding with frontend logout:', data.message);
      }
    } catch (error) {
      console.error('Logout API error, but proceeding with frontend logout:', error);
    } finally {
      // Always perform frontend logout
      logout(); // Use context logout
      
      // Reset all form state variables
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setName('');
      setVerificationCode('');
      setMessage('');
      setShowVerification(false);
      setShowForgotPassword(false);
      setShowResetForm(false);
      setResetEmail('');
      setResetCode('');
      setNewPassword('');
      setConfirmNewPassword('');
      
      console.log('Frontend logout completed');
    }
  };

  // FIXED: Handle authentication with proper error handling
  // FIXED: Handle authentication with proper loading state
const handleAuth = async (e) => {
  e.preventDefault();
  e.stopPropagation(); // IMPORTANT: Stop event bubbling
  
  // Log AFTER the check, not before
  console.log('🔍 handleAuth called, isSubmitting:', isSubmitting);
  
  // Prevent double submission - CHECK THIS FIRST
  if (isSubmitting) {
    console.log('⏳ Already submitting, ignoring duplicate call');
    return;
  }
  
  setIsSubmitting(true);
  setMessage('');
  
  try {
    if (isLogin) {
      console.log('🔐 Attempting login for:', email);
      console.log('📡 API URL:', `${API_BASE_URL}/api/login`);
      
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      console.log('📋 Response status:', response.status);
      
      const responseText = await response.text();
      console.log('📋 Raw response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
        console.log('📋 Parsed response data:', data);
      } catch (parseError) {
        console.error('❌ JSON parse error:', parseError);
        setMessage('Invalid response from server');
        return;
      }
      
      if (response.ok) {
        console.log('✅ Login successful');
        login(data.user, data.token);
        localStorage.setItem('token', data.token);
        localStorage.setItem('userData', JSON.stringify(data.user));
        setShowAuthModal(false);
        setMessage('Login successful!');
        
        // Clear form
        setEmail('');
        setPassword('');
      } else {
        console.log('❌ Login failed with data:', data);
        
        if (data.alreadyLoggedIn) {
          console.log('🚫 Already logged in detected');
          setMessage('You are already logged in on another device. Please use force logout below to continue.');
        } else if (data.requiresVerification) {
          setShowVerification(true);
          setMessage('Please verify your email first.');
        } else {
          setMessage(data.message || `Login failed (Status: ${response.status})`);
        }
      }
    } else {
      // Registration logic
      console.log('📝 Attempting registration for:', email);
      
      if (password !== confirmPassword) {
        setMessage('Passwords do not match');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, password }),
      });

      const data = await response.json();
      
      if (response.ok) {
        console.log('✅ Registration successful');
        setShowVerification(true);
        setMessage('Registration successful! Please check your email for verification code.');
        
        // Clear form except email (needed for verification)
        setPassword('');
        setConfirmPassword('');
        setName('');
      } else {
        console.log('❌ Registration failed:', data);
        
        // Better error handling for existing user
        if (data.message === 'User already exists') {
          setMessage('This email is already registered. Please login instead or use a different email.');
          // Switch to login mode
          setTimeout(() => {
            setIsLogin(true);
            setPassword('');
            setConfirmPassword('');
            setName('');
            setMessage('Switched to login. Please enter your password.');
          }, 2000);
        } else {
          setMessage(data.message || 'Registration failed');
        }
      }
    }
  } catch (error) {
    console.error('💥 Auth error:', error);
    setMessage(`Network error: ${error.message}`);
  } finally {
    console.log('🔄 Resetting isSubmitting to false');
    setIsSubmitting(false);
  }
};

const handleVerifyCode = async (e) => {
  e.preventDefault();
  e.stopPropagation();
  
  if (isSubmitting) {
    console.log('⏳ Already submitting verification');
    return;
  }
  
  setIsSubmitting(true);
  setMessage('');
  
  try {
    if (!verificationCode || verificationCode.length !== 6) {
      setMessage('Please enter a valid 6-digit code');
      return;
    }

    const response = await fetch(`${API_BASE_URL}/api/verify-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, code: verificationCode }),
    });

    const data = await response.json();
    
    if (response.ok) {
      console.log('✅ Verification successful');
      login(data.user, data.token);
      localStorage.setItem('token', data.token);
      localStorage.setItem('userData', JSON.stringify(data.user));
      setShowAuthModal(false);
      setMessage('Verification successful!');
      
      // Clear form
      setEmail('');
      setVerificationCode('');
    } else {
      console.log('❌ Verification failed:', data);
      setMessage(data.message || 'Verification failed');
    }
  } catch (error) {
    console.error('💥 Verification error:', error);
    setMessage(`Network error: ${error.message}`);
  } finally {
    setIsSubmitting(false);
  }
};

  // FIXED: Force logout function with better error handling
  const handleForceLogout = async () => {
    try {
      console.log('🔓 Attempting force logout for:', email);
      
      if (!email) {
        setMessage('Please enter your email address first');
        return;
      }
      
      // Prevent if already submitting
      if (isSubmitting) {
        return;
      }
      
      setIsSubmitting(true);
      setMessage('Forcing logout from all devices...');
      
      const response = await fetch(`${API_BASE_URL}/api/force-logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email }),
      });

      console.log('Force logout response status:', response.status);
      
      const data = await response.json();
      console.log('Force logout response data:', data);
      
      if (response.ok) {
        setMessage('✅ Force logout successful! Please click Login button to login again.');
        // DON'T auto-retry login - let user click the button
      } else {
        setMessage(data.message || 'Force logout failed. Please try again.');
      }
    } catch (error) {
      console.error('Force logout error:', error);
      setMessage('Network error during force logout. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  //  resendVerificationCode in your frontend with:
  const resendVerificationCode = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),  // just send email, server generates code
      });

      const data = await response.json();
      
      if (response.ok) {
        setMessage('New verification code sent to your email.');
      } else {
        setMessage(data.message || 'Failed to resend verification code');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!resetEmail) {
      setMessage('Please enter your email address');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setShowResetForm(true);
        setMessage('Reset code sent to your email. Please check your inbox.');
      } else {
        setMessage(data.message || 'Failed to send reset code');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setMessage('Network error. Please try again.');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMessage('');

    if (newPassword !== confirmNewPassword) {
      setMessage('Passwords do not match');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: resetEmail, 
          code: resetCode,
          newPassword 
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setMessage('Password reset successfully! You can now login with your new password.');
        setShowForgotPassword(false);
        setShowResetForm(false);
        setResetEmail('');
        setResetCode('');
        setNewPassword('');
        setConfirmNewPassword('');
      } else {
        setMessage(data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setMessage('Network error. Please try again.');
    }
  };

  const resendResetCode = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resend-reset-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setMessage('New reset code sent to your email.');
      } else {
        setMessage(data.message || 'Failed to resend reset code');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
    }
  };

  // ENHANCED: Add session status check function (for debugging)
  const checkSessionStatus = async (userEmail) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/session-status/${userEmail}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const sessionData = await response.json();
        console.log('📊 Current session status:', sessionData);
        return sessionData;
      }
    } catch (error) {
      console.error('Failed to check session status:', error);
    }
    return null;
  };

  const closeAuthModal = () => {
    setShowAuthModal(false);
    setShowVerification(false);
    setShowForgotPassword(false);
    setShowResetForm(false);
    setIsLogin(true);
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setName('');
    setVerificationCode('');
    setResetEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmNewPassword('');
    setMessage('');
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setIsSubmitting(true);
      setMessage('');

      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential }),
      });

      const data = await response.json();

      if (response.ok) {
        login(data.user, data.token);
        localStorage.setItem('token', data.token);
        localStorage.setItem('userData', JSON.stringify(data.user));
        setShowAuthModal(false);
      } else {
        setMessage(data.message || 'Google login failed');
      }
    } catch (error) {
      setMessage('Google login error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProductClick = (path) => {
    if (!user) {
      setShowAuthModal(true);
      setMessage('Please login to access this feature');
    } else {
      navigate(path);
      window.scrollTo(0, 0);
    }
  };

    // Add handleContactSubmit function
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    setContactMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm),
      });

      const data = await response.json();
      
      if (response.ok) {
        setContactMessage('Message sent successfully! We\'ll get back to you soon.');
        setContactForm({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setContactMessage(data.message || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setContactMessage('Network error. Please try again later.');
    } finally {
      setContactSubmitting(false);
    }
  };

  // Handle loading state FIRST
  if (loading) {
    return (
      <div className="neural-glass-homepage">
        {showProfileModal && (
          <ProfileSetupModal onComplete={() => setShowProfileModal(false)} />
        )}
        <div className="neural-background"></div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          color: '#fff',
          fontSize: '1.2rem'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="neural-glass-homepage">
       {/* Profile Setup Modal */}
      {showProfileModal && (
        <ProfileSetupModal onComplete={() => setShowProfileModal(false)} />
      )}
      {/* Auth Modal */}
      {showAuthModal && (
        <div className="auth-modal-overlay">
          <div className="auth-modal glass">
            <button className="close-modal" onClick={closeAuthModal}>×</button>
            
            {showVerification ? (
              <>
                <h2>Verify Your Email</h2>
                <p>Enter the 6-digit code sent to your email</p>
                <form onSubmit={handleVerifyCode}>
                  <div className="form-group">
                    <input
                      type="text"
                      placeholder="Verification Code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      required
                      maxLength="6"
                    />
                  </div>
                  
                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={isSubmitting}
                    style={{
                      opacity: isSubmitting ? 0.6 : 1,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isSubmitting ? 'Verifying...' : 'Verify & Login'}
                  </button>
                </form>
                
                <p className="auth-switch">
                  Didn't receive the code?{' '}
                  <button 
                    type="button" 
                    className="auth-toggle"
                    onClick={resendVerificationCode}
                  >
                    Resend Code
                  </button>
                </p>

                {/* FIXED: Add force logout option */}
                {message && (
                  <div className={`auth-message ${
                    message.includes('already logged in') || message.includes('another device') 
                      ? 'auth-message-error' 
                      : message.includes('successful') || message.includes('✅')
                        ? 'auth-message-success'
                        : ''
                  }`}>
                    {message}
                    
                    {/* Show force logout option for "already logged in" messages */}
                    {(message.includes('already logged in') || message.includes('another device')) && (
                      <div style={{ 
                        marginTop: '15px', 
                        textAlign: 'center',
                        padding: '10px',
                        backgroundColor: 'rgba(255, 107, 107, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 107, 107, 0.3)'
                      }}>
                        <p style={{ 
                          fontSize: '0.9rem', 
                          color: '#ff6b6b', 
                          margin: '0 0 10px 0' 
                        }}>
                          This will log you out from all other devices and allow you to login here.
                        </p>
                        <button 
                          type="button" 
                          className="force-logout-btn"
                          onClick={handleForceLogout}
                          disabled={isSubmitting}
                          style={{ 
                            color: '#fff',
                            backgroundColor: isSubmitting ? '#999' : '#ff6b6b',
                            fontSize: '0.9rem',
                            border: `1px solid ${isSubmitting ? '#999' : '#ff6b6b'}`,
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                            fontWeight: '600',
                            transition: 'all 0.3s ease',
                            opacity: isSubmitting ? 0.6 : 1
                          }}
                        >
                          {isSubmitting ? 'Processing...' : '🔓 Force Logout & Login Here'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : showForgotPassword ? (
            <>
              <h2>{showResetForm ? 'Reset Password' : 'Forgot Password'}</h2>
              
              {!showResetForm ? (
                <>
                  <p>Enter your email to receive a password reset code</p>
                  <form onSubmit={handleForgotPassword}>
                    <div className="form-group">
                      <input
                        type="email"
                        placeholder="Email Address"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                      />
                    </div>
                    
                    <button 
                      type="submit" 
                      className="submit-btn"
                      disabled={isSubmitting}
                      style={{
                        opacity: isSubmitting ? 0.6 : 1,
                        cursor: isSubmitting ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Reset Code'}
                    </button>
                  </form>
                </>
              ) : (
                <>
                  <p>Enter the reset code sent to {resetEmail} and your new password</p>
                  <form onSubmit={handleResetPassword}>
                    <div className="form-group">
                      <input
                        type="text"
                        placeholder="Reset Code (6 digits)"
                        value={resetCode}
                        onChange={(e) => setResetCode(e.target.value)}
                        required
                        maxLength="6"
                      />
                    </div>
                    <div className="form-group">
                      <input
                        type="password"
                        placeholder="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength="6"
                      />
                    </div>
                    <div className="form-group">
                      <input
                        type="password"
                        placeholder="Confirm New Password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        required
                        minLength="6"
                      />
                    </div>
                    
                    <button 
                      type="submit" 
                      className="submit-btn"
                      disabled={isSubmitting}
                      style={{
                        opacity: isSubmitting ? 0.6 : 1,
                        cursor: isSubmitting ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isSubmitting ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </form>
                  
                  <p className="auth-switch">
                    Didn't receive the code?{' '}
                    <button 
                      type="button" 
                      className="auth-toggle"
                      onClick={resendResetCode}
                      disabled={isSubmitting}
                    >
                      Resend Code
                    </button>
                  </p>
                </>
              )}
              
              {/* Add message display */}
              {message && (
                <div className={`auth-message ${
                  message.includes('successful') || message.includes('sent') 
                    ? 'auth-message-success'
                    : 'auth-message-error'
                }`}>
                  {message}
                </div>
              )}
              
              <p className="auth-switch">
                Remember your password?{' '}
                <button 
                  type="button" 
                  className="auth-toggle"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setShowResetForm(false);
                    setResetEmail('');
                    setResetCode('');
                    setNewPassword('');
                    setConfirmNewPassword('');
                    setMessage('');
                  }}
                >
                  Back to Login
                </button>
              </p>
            </>
            ) : (
              <>
                <h2>{isLogin ? 'Login' : 'Create Account'}</h2>

                {/* Google Login Button */}
                <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0 8px' }}>
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => setMessage('Google login failed. Please try again.')}
                    theme="filled_black"
                    shape="rectangular"
                    width="320"
                    text={isLogin ? 'signin_with' : 'signup_with'}
                  />
                </div>

                {/* Divider */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  margin: '16px 0', color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem'
                }}>
                  <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.15)' }} />
                  or continue with email
                  <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.15)' }} />
                </div>
                
                <form onSubmit={handleAuth}>
                  {!isLogin && (
                    <div className="form-group">
                      <input
                        type="text"
                        placeholder="Full Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </div>
                  )}
                  
                  <div className="form-group">
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                  
                  {!isLogin && (
                    <div className="form-group">
                      <input
                        type="password"
                        placeholder="Confirm Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                      />
                    </div>
                  )}
                  
                  {isLogin && (
                    <div className="forgot-password-link">
                      <button 
                        type="button" 
                        className="auth-toggle"
                        onClick={() => setShowForgotPassword(true)}
                      >
                        Forgot Password?
                      </button>
                    </div>
                  )}
                  
                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={isSubmitting}
                    style={{
                      opacity: isSubmitting ? 0.6 : 1,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isSubmitting ? 'Processing...' : (isLogin ? 'Login' : 'Register')}
                  </button>
                </form>
                
                {/* Display message with force logout option */}
                {message && (
                  <div className={`auth-message ${
                    message.includes('already logged in') || message.includes('another device') 
                      ? 'auth-message-error' 
                      : message.includes('successful') || message.includes('✅')
                        ? 'auth-message-success'
                        : ''
                  }`}>
                    {message}
                    
                    {/* Show force logout option for "already logged in" messages */}
                    {(message.includes('already logged in') || message.includes('another device')) && (
                      <div style={{ 
                        marginTop: '15px', 
                        textAlign: 'center',
                        padding: '10px',
                        backgroundColor: 'rgba(255, 107, 107, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 107, 107, 0.3)'
                      }}>
                        <p style={{ 
                          fontSize: '0.9rem', 
                          color: '#ff6b6b', 
                          margin: '0 0 10px 0' 
                        }}>
                          This will log you out from all other devices and allow you to login here.
                        </p>
                        <button 
                          type="button" 
                          className="force-logout-btn"
                          onClick={handleForceLogout}
                          disabled={isSubmitting}
                          style={{ 
                            color: '#fff',
                            backgroundColor: isSubmitting ? '#999' : '#ff6b6b',
                            fontSize: '0.9rem',
                            border: `1px solid ${isSubmitting ? '#999' : '#ff6b6b'}`,
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                            fontWeight: '600',
                            transition: 'all 0.3s ease',
                            opacity: isSubmitting ? 0.6 : 1
                          }}
                        >
                          {isSubmitting ? 'Processing...' : '🔓 Force Logout & Login Here'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                <p className="auth-switch">
                  {isLogin ? "Don't have an account? " : 'Already have an account? '}
                  <button 
                    type="button" 
                    className="auth-toggle"
                    onClick={() => setIsLogin(!isLogin)}
                  >
                    {isLogin ? 'Register' : 'Login'}
                  </button>
                </p>
              </>
            )}
          </div>
        </div>
      )}

      
      {/* Rest of your JSX - keep everything the same from here */}
      <div className="neural-background"></div>
      
      <div className="geometric-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>

      <div className="neural-lines">
        <div className="neural-line line-1"></div>
        <div className="neural-line line-2"></div>
        <div className="neural-line line-3"></div>
      </div>

      <Header setShowAuthModal={setShowAuthModal} />

      {/* Keep the rest of your sections exactly as they are */}
      <section className='hero' id='showcase'>
        <div className='hero-split'>
          {/* Left: AI Assistant content */}
          <div className='hero-content'>
            <h1>#1 Real-Time <br></br> AI Career Copilot</h1>
            
            <div className='hero-description'>
              <p>Practice interviews, generate answers in real time, optimize your resume, and auto-apply to 100+ jobs daily.</p>
            </div>

            <div className='hero-stats'>
              <div className='hero-stat hero-stat-download'>
                <span className='hero-stat-number'>
                  <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                  </svg>
                </span>
                <span className='hero-stat-label'>50,000+ Users</span>
              </div>
              <div className='hero-stat-download-wrapper'>
                <div className='hero-stat hero-stat-download' onClick={() => window.location.href = '/downloads/TERVIEW-1.0.0-mac.dmg'}>
                  <span className='hero-stat-number'>
                    <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
                      <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 17 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5M13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
                    </svg>
                  </span>
                  <span className='hero-stat-label'>Mac App (.dmg)</span>
                </div>
                <button className='audio-setup-link' onClick={(e) => { e.stopPropagation(); setAudioModal('mac'); }}>
                  How to setup audio
                </button>
              </div>
              <div className='hero-stat-download-wrapper'>
                <div className='hero-stat hero-stat-download' onClick={() => window.location.href = '/downloads/TERVIEW Setup 1.0.0.exe'}>
                  <span className='hero-stat-number'>
                    <svg viewBox="0 0 48 48" width="40" height="40" fill="currentColor">
                      <path d="M20.016 4.008L4.008 6.504v14.496h16.008V4.008zM44.016 2.016L24 5.016V21h20.016V2.016zM44.016 24H24v15.984l20.016 3.001V24zM20.016 24H4.008v14.496l16.008 2.496V24z"/>
                    </svg>
                  </span>
                  <span className='hero-stat-label'>Windows App (.exe)</span>
                </div>
                <button className='audio-setup-link' onClick={(e) => { e.stopPropagation(); setAudioModal('windows'); }}>
                  How to setup audio
                </button>
              </div>
              <div className='hero-stat hero-stat-download'>
                <span className='hero-stat-number'>
                  <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
                    <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                  </svg>
                </span>
                <span className='hero-stat-label'>Stealth Mode</span>
              </div>
            </div>
            
            <div className='cta-buttons'>
              <button 
                className='cta-button'
                onClick={() => handleProductClick("/InterviewAssistant")}
              >
                Start Interview Assistant
              </button>
              <a href='#features' className='cta-button secondary'>Explore Features</a>
            </div>
          </div>

          {/* Right: Products grid - same hexagon styling as showcase */}
          <div className='hero-products'>
            <div className='hexagon-container hero-hexagon-container'>
              <div className='hexagon' onClick={() => handleProductClick('/InterviewAssistant')}>
                <div className='hexagon-inner glass'>
                  <div className='hexagon-icon'>🏆</div>
                  <h4>Interview Assistant</h4>
                </div>
              </div>
              <div className='hexagon' onClick={() => handleProductClick('/mockInterview')}>
                <div className='hexagon-inner glass'>
                  <div className='hexagon-icon'>🔮</div>
                  <h4>Mock AI Interview</h4>
                </div>
              </div>
              <div className='hexagon' onClick={() => handleProductClick('/AutoJobApply')}>
                <div className='hexagon-inner glass'>
                  <div className='hexagon-icon'>⚡</div>
                  <h4>Auto Apply</h4>
                </div>
              </div>
              <div className='hexagon' onClick={() => handleProductClick('/BuildResume')}>
                <div className='hexagon-inner glass'>
                  <div className='hexagon-icon'>📄</div>
                  <h4>Resume Builder</h4>
                </div>
              </div>
              <div className='hexagon' onClick={() => handleProductClick('/InterviewQuestions')}>
                <div className='hexagon-inner glass'>
                  <div className='hexagon-icon'>💎</div>
                  <h4>Interview Questions</h4>
                </div>
              </div>
              <div className='hexagon' onClick={() => handleProductClick('/CoverLetter')}>
                <div className='hexagon-inner glass'>
                  <div className='hexagon-icon'>🛡️</div>
                  <h4>Cover Letter</h4>
                </div>
              </div>
              <div className='hexagon' onClick={() => handleProductClick('/ATSCheck')}>
                <div className='hexagon-inner glass'>
                  <div className='hexagon-icon'>⭐</div>
                  <h4>ATS Check</h4>
                </div>
              </div>
              <div className='hexagon' onClick={() => handleProductClick('/portfolio')}>
                <div className='hexagon-inner glass'>
                  <div className='hexagon-icon'>🚀</div>
                  <h4>Portfolio Builder</h4>
                </div>
              </div>
              <div className='hexagon' onClick={() => handleProductClick('/certification')}>
                <div className='hexagon-inner glass'>
                  <div className='hexagon-icon'>🎓</div>
                  <h4>Course Creator</h4>
                </div>
              </div>
              <div className='hexagon' onClick={() => handleProductClick('/LinkedInOptimizer')}>
                <div className='hexagon-inner glass'>
                  <div className='hexagon-icon'>📈</div>
                  <h4>Linkedin Optimizer</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Logos Scroll Section */}
      <section className='trusted-by'>
        <h3 className='trusted-title'>Trusted by professionals at</h3>
        <div className='logos-scroll-container'>
          <div className='logos-scroll'>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg' alt='Google' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg' alt='Amazon' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg' alt='Microsoft' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg' alt='Apple' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg' alt='Netflix' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/e/e1/Logo_of_YouTube_%282015-2017%29.svg' alt='YouTube' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg' alt='Meta' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/c/c9/Linkedin.svg' alt='LinkedIn' />
            </div>
            {/* Duplicate set for seamless loop */}
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg' alt='Google' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg' alt='Amazon' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg' alt='Microsoft' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg' alt='Apple' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg' alt='Netflix' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/e/e1/Logo_of_YouTube_%282015-2017%29.svg' alt='YouTube' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/b/b1/Meta_Platforms_Inc._logo.svg' alt='Meta' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/c/c9/Linkedin.svg' alt='LinkedIn' />
            </div>
          </div>
        </div>
      </section>




      <section className='features' id='features'>
        <AIFeaturesSection 
          user={user}
          setShowAuthModal={setShowAuthModal}
          setMessage={setMessage}
        />
      </section>

              {/* AI Technologies Scroll Section */}
      <section className='ai-tech-section'>
        <h3 className='trusted-title'>Leading AI Technologies</h3>
        <div className='logos-scroll-container'>
          <div className='logos-scroll logos-scroll-reverse'>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg' alt='ChatGPT' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/8/8a/Google_Gemini_logo.svg' alt='Google Gemini' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/c/c6/PyTorch_logo_black.svg' alt='PyTorch' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/3/38/Jupyter_logo.svg' alt='Jupyter' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/e/ed/Pandas_logo.svg' alt='Pandas' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg' alt='ChatGPT' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/8/8a/Google_Gemini_logo.svg' alt='Google Gemini' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/c/c6/PyTorch_logo_black.svg' alt='PyTorch' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/8/8a/Claude_AI_logo.svg' alt='Claude' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/1/1d/Perplexity_AI_logo.svg' alt='Perplexity' />
            </div>
            <div className='logo-item'>
              <img src='https://upload.wikimedia.org/wikipedia/commons/e/ec/DeepSeek_logo.svg' alt='Deep-Seek' />
            </div>
          </div>
        </div>
      </section>

      {/*
      <section className="timeline" id="timeline">
          <h2 className="section-title">EVOLUTION TIMELINE</h2>
          <div className="timeline-container">
              <div className="timeline-line"></div>
              
              <div className="timeline-item">
                  <div className="timeline-content glass" style={{opacity: 1, transform: "translateY(0px)", transition: "opacity 0.8s, transform 0.8s"}}>
                      <div className="timeline-year">2024</div>
                      <h4>Genesis Protocol</h4>
                      <p>NeuralGlass is a cutting-edge cyberpunk glassmorphism HTML template featuring a striking purple, pink, and green color palette with futuristic neural network aesthetics.</p>
                  </div>
                  <div className="timeline-dot"></div>
              </div>
              
              <div className="timeline-item">
                  <div className="timeline-content glass" style={{opacity: 1, transform: "translateY(0px)", transition: "opacity 0.8s, transform 0.8s"}}>
                      <div className="timeline-year">2025</div>
                      <h4>Matrix Integration</h4>
                      <p>Built with pure HTML/CSS and vanilla JavaScript, it offers smooth scrolling, mobile-responsive navigation with hamburger menu, parallax effects, and dynamic visual elements that respond to user interaction.</p>
                  </div>
                  <div className="timeline-dot"></div>
              </div>
              
              <div className="timeline-item">
                  <div className="timeline-content glass" style={{opacity: 1, transform: "translateY(0px)", transition: "opacity 0.8s, transform 0.8s"}}>
                      <div className="timeline-year">2026</div>
                      <h4>Quantum Leap</h4>
                      <p>This HTML CSS template is brought to you by TemplateMo website. Perfect for tech startups, AI companies, gaming platforms, or any project requiring a bold, futuristic web presence.</p>
                  </div>
                  <div className="timeline-dot"></div>
              </div>
              
              <div className="timeline-item">
                  <div className="timeline-content glass" style={{opacity: 1, transform: "translateY(0px)", transition: "opacity 0.8s, transform 0.8s"}}>
                      <div className="timeline-year">2027</div>
                      <h4>Neural Singularity</h4>
                      <p>Human consciousness successfully merges with AI systems, creating hybrid intelligences that transcend biological limitations.</p>
                  </div>
                  <div className="timeline-dot"></div>
              </div>
          </div>
      </section>
      */}

      <Testimonials />

      <FAQ />

      <section className='contact' id='contact'>
        <h1>Contact Us</h1>
        <br></br>
        <div className='contact-container'>
          <div className='contact-info glass'>
            <div className='developer-profile'>
              <h3>Connect with Team!</h3>
            </div>
            <p>Have questions about our AI career tools?</p>
            <p>
              Send us a message and let's accelerate your career journey together. 
              Whether you're curious about Interview Assistant, Resume Builder, or 
              any of our tools — our team is always happy to connect with fellow 
              career seekers and enthusiasts.
            </p>
            <div className='social-links'>
              <a href='https://linkedin.com/in/yourprofile' target='_blank' rel='noopener noreferrer' className='social-link glass' title='LinkedIn'>
                💼
              </a>
              <a href='https://github.com/yourprofile' target='_blank' rel='noopener noreferrer' className='social-link glass' title='GitHub'>
                💻
              </a>
              <a href='https://twitter.com/yourprofile' target='_blank' rel='noopener noreferrer' className='social-link glass' title='Twitter'>
                🐦
              </a>
              <a href='mailto:your.email@example.com' className='social-link glass' title='Email'>
                📨
              </a>
            </div>
          </div>
          
          <div className='contact-form glass'>
            <form onSubmit={handleContactSubmit}>
              <div className='form-group'>
                <input 
                  type='text' 
                  placeholder='(Name)' 
                  value={contactForm.name}
                  onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                  required 
                />
              </div>
              <div className='form-group'>
                <input 
                  type='email' 
                  placeholder='(Email)' 
                  value={contactForm.email}
                  onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                  required 
                />
              </div>
              <div className='form-group'>
                <input 
                  type='text' 
                  placeholder='(Subject)' 
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                  required 
                />
              </div>
              <div className='form-group'>
                <textarea 
                  rows='5' 
                  placeholder='(Message)' 
                  value={contactForm.message}
                  onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                  required
                />
              </div>
              
              {contactMessage && (
                <div className={`contact-message ${contactMessage.includes('success') ? 'success' : 'error'}`}>
                  {contactMessage}
                </div>
              )}
              
              <button 
                type='submit' 
                className='submit-btn' 
                disabled={contactSubmitting}
                style={{
                  opacity: contactSubmitting ? 0.6 : 1,
                  cursor: contactSubmitting ? 'not-allowed' : 'pointer'
                }}
              >
                {contactSubmitting ? 'TRANSMITTING...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer></Footer>

      {/* Audio Setup Modal */}
      {audioModal && (
        <div className='audio-modal-backdrop' onClick={() => setAudioModal(null)}>
          <div className='audio-modal' onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className='audio-modal-header'>
              <div className='audio-modal-header-icon'>
                {audioModal === 'mac' ? (
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                    <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 17 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5M13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 48 48" width="22" height="22" fill="currentColor">
                    <path d="M20.016 4.008L4.008 6.504v14.496h16.008V4.008zM44.016 2.016L24 5.016V21h20.016V2.016zM44.016 24H24v15.984l20.016 3.001V24zM20.016 24H4.008v14.496l16.008 2.496V24z"/>
                  </svg>
                )}
              </div>
              <div>
                <h2 className='audio-modal-title'>
                  {audioModal === 'mac' ? 'Mac Audio Setup' : 'Windows Audio Setup'}
                </h2>
                <p className='audio-modal-badge'>
                  {audioModal === 'mac' ? 'via BlackHole 2ch (free)' : 'via VB-Cable (free)'}
                </p>
              </div>
              <button className='audio-modal-close' onClick={() => setAudioModal(null)}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <p className='audio-modal-subtitle'>
              {audioModal === 'mac'
                ? 'BlackHole routes your system audio into the app as a virtual microphone.'
                : 'VB-Cable routes your system audio into the app as a virtual microphone.'}
            </p>

            {/* Steps */}
            <ol className='audio-modal-steps'>
              {(audioModal === 'mac' ? [
                {
                  title: 'Download BlackHole 2ch',
                  desc: <>Go to <span className='audio-pill'>existential.audio/blackhole</span> and download the free <span className='audio-pill'>BlackHole 2ch</span> installer.</>
                },
                {
                  title: 'Run the installer',
                  desc: <>Open the <span className='audio-pill'>.pkg</span> file and follow the steps. Restart your Mac if prompted.</>
                },
                {
                  title: 'Create a Multi-Output Device',
                  desc: <>Open <span className='audio-pill'>Audio MIDI Setup</span> (Spotlight → search). Click <span className='audio-pill'>+</span> → <em>Create Multi-Output Device</em>. Check <span className='audio-pill'>BlackHole 2ch</span> and your speakers.</>
                },
                {
                  title: 'Set as system output',
                  desc: <>Go to <span className='audio-pill'>System Settings → Sound → Output</span> and select <em>Multi-Output Device</em>.</>
                },
                {
                  title: 'Select in Terview',
                  desc: <>Open the app, go to Interview Assistant, and pick <span className='audio-pill'>BlackHole 2ch</span> as the audio input. Done!</>
                },
              ] : [
                {
                  title: 'Download VB-Cable',
                  desc: <>Go to <span className='audio-pill'>vb-audio.com/Cable</span> and download the free <span className='audio-pill'>VBCABLE_Driver_Pack43.zip</span>.</>
                },
                {
                  title: 'Install the driver',
                  desc: <>Extract the zip, right-click <span className='audio-pill'>VBCABLE_Setup_x64.exe</span> → <em>Run as Administrator</em>. Install and restart your PC.</>
                },
                {
                  title: 'Set CABLE Input as output',
                  desc: <>Right-click the taskbar speaker → <span className='audio-pill'>Sound Settings</span>. Under Output, select <span className='audio-pill'>CABLE Input (VB-Audio)</span>.</>
                },
                {
                  title: 'Keep hearing audio (optional)',
                  desc: <>In Sound → Playback, right-click <em>CABLE Input</em> → Properties → Listen tab → check <em>Listen to this device</em> and pick your speakers.</>
                },
                {
                  title: 'Select in Terview',
                  desc: <>Open the app, go to Interview Assistant, and pick <span className='audio-pill'>CABLE Output</span> as the audio input. Done!</>
                },
              ]).map((step, i) => (
                <li key={i} className='audio-step'>
                  <span className='step-num'>{i + 1}</span>
                  <div className='step-body'>
                    <span className='step-title'>{step.title}</span>
                    <span className='step-desc'>{step.desc}</span>
                  </div>
                </li>
              ))}
            </ol>

          </div>
        </div>
      )}
    </div>

  );
};

export default NeuralGlassHomepage;